import { type KeyboardEvent, useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { local } from "@/lib/storage";
import { useSession } from "../session.tsx";

/** Kept under the person's ID, so two people sharing a browser keep their own width. */
export const pagesWidthKey = (userId: string) => `esi.wiki.pages-width.${userId.toLowerCase()}`;
/**
 * The Pages panel: 240px by default, between 200px (titles stay legible) and 400px (#418), and
 * never more than a quarter of the window, so the reading column keeps its room.
 */
export const PAGES_MIN_WIDTH = 200;
export const PAGES_MAX_WIDTH = 400;
export const PAGES_DEFAULT_WIDTH = 240;
const STEP = 16;
const LARGE_STEP = 64;

const clamp = (width: number) =>
  Math.round(Math.max(PAGES_MIN_WIDTH, Math.min(PAGES_MAX_WIDTH, width)));
/** The widest the panel may be in this window (the chosen width is kept, only shown narrower). */
const maxWidth = () =>
  Math.max(PAGES_MIN_WIDTH, Math.min(PAGES_MAX_WIDTH, Math.floor(window.innerWidth / 4)));
const onResize = (notify: () => void) => {
  window.addEventListener("resize", notify);
  return () => window.removeEventListener("resize", notify);
};
/** The bound follows the window as it resizes, so the value and the keys match what is shown. */
const usePagesMaxWidth = () => useSyncExternalStore(onResize, maxWidth);

/**
 * The Pages panel's width, remembered per person in this browser (a convenience, never
 * required: without storage the width lasts for this page). A kept value out of bounds is
 * clamped; anything unreadable falls back to the default.
 */
export function usePagesWidth(): {
  width: number;
  set: (next: number, persist?: boolean) => void;
  reset: () => void;
} {
  const key = pagesWidthKey(useSession().userId);
  const [width, setWidth] = useState(() => {
    const raw = local.get(key);
    const kept = raw === null ? Number.NaN : Number(raw);
    return Number.isFinite(kept) ? clamp(kept) : PAGES_DEFAULT_WIDTH;
  });
  return {
    width,
    set: (next, persist = true) => {
      const clamped = clamp(next);
      setWidth(clamped);
      if (persist) {
        local.set(key, String(clamped));
      }
    },
    reset: () => {
      setWidth(PAGES_DEFAULT_WIDTH);
      local.set(key, null);
    },
  };
}

/**
 * The Pages panel's right edge (#418): a focusable vertical separator. Drag it, move it with
 * the arrow keys (Shift for larger steps), Home and End for the bounds; a double-click resets
 * the default width. A drag is remembered once, when it ends.
 */
export function PagesSplitter(props: {
  width: number;
  onWidth: (width: number, persist?: boolean) => void;
  onReset: () => void;
}) {
  const { width, onWidth, onReset } = props;
  const { t } = useTranslation();
  const max = usePagesMaxWidth();
  const shown = Math.min(width, max);
  const onKeyDown = (event: KeyboardEvent) => {
    // Alt, Ctrl and Cmd shortcuts (history, text navigation) stay the browser's.
    if (event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }
    const step = event.shiftKey ? LARGE_STEP : STEP;
    const next =
      event.key === "ArrowLeft"
        ? shown - step
        : event.key === "ArrowRight"
          ? Math.min(shown + step, max)
          : event.key === "Home"
            ? PAGES_MIN_WIDTH
            : event.key === "End"
              ? max
              : null;
    if (next !== null) {
      event.preventDefault();
      onWidth(next);
    }
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: an interactive window splitter, not a thematic break
    <div
      role="separator"
      tabIndex={0}
      aria-label={t("wiki.docs.resize")}
      aria-orientation="vertical"
      aria-valuemin={PAGES_MIN_WIDTH}
      aria-valuemax={max}
      aria-valuenow={shown}
      aria-valuetext={t("wiki.docs.resizeValue", { width: shown })}
      title={t("wiki.docs.resizeHint")}
      className="group relative z-10 w-px shrink-0 cursor-col-resize touch-none bg-line outline-none hover:bg-line-strong focus-visible:outline-none data-[dragging=true]:bg-line-strong"
      onKeyDown={onKeyDown}
      onDoubleClick={(event) => {
        event.preventDefault();
        onReset();
      }}
      onPointerDown={(event) => {
        // Only the primary button drags; a double-click's presses end unmoved, then reset.
        if (event.button !== 0) {
          return;
        }
        // No text selection while dragging (click and dblclick still fire).
        event.preventDefault();
        const handle = event.currentTarget;
        handle.focus();
        try {
          handle.setPointerCapture?.(event.pointerId);
        } catch {
          // The pointer is already gone: the drag still follows the handle's own events.
        }
        handle.dataset.dragging = "true";
        const startX = event.clientX;
        const start = shown;
        let last = start;
        let moved = false;
        const move = (e: PointerEvent) => {
          e.preventDefault();
          // A click's jitter without horizontal travel is not a resize.
          if (e.clientX === startX && !moved) {
            return;
          }
          moved = true;
          last = Math.min(start + (e.clientX - startX), max);
          onWidth(last, false);
        };
        // Released, cancelled or lost: the drag ends, and a moved width is remembered once.
        const end = () => {
          handle.removeEventListener("pointermove", move);
          handle.removeEventListener("pointerup", end);
          handle.removeEventListener("pointercancel", end);
          handle.removeEventListener("lostpointercapture", end);
          delete handle.dataset.dragging;
          if (moved) {
            onWidth(last);
          }
        };
        handle.addEventListener("pointermove", move);
        handle.addEventListener("pointerup", end);
        handle.addEventListener("pointercancel", end);
        handle.addEventListener("lostpointercapture", end);
      }}
    >
      <span aria-hidden="true" className="absolute inset-y-0 -left-1.5 w-3" />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-1/2 h-8 w-1 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-transparent transition-colors duration-(--duration-fast) group-hover:bg-line-strong group-focus-visible:bg-surface group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-data-[dragging=true]:bg-line-strong"
      />
    </div>
  );
}
